import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import GallerySection from "../components/GallerySection";
import IntroSection from "../components/IntroSection";
import { homeGalleryGroups } from "../data/galleries";
import { useGalleryFeed } from "../hooks/useSyncedImages";
import GalleryLoadMore from '../components/GalleryLoadMore';

export default function Home() {
  const [selected, setSelected] = useState("Home");
  const galleryRef = useRef(null);
  const folder = { Weddings: 'images/weddings', 'Birthday Parties': 'images/birthday' }[selected];
  const feed = useGalleryFeed(folder, homeGalleryGroups[selected] || []);

  React.useEffect(() => {
    if (selected !== "Home" && galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selected]);

  return (
    <div className="font-serif">
      <Header selected={selected} setSelected={setSelected} />

      <IntroSection
        icon="🌹🌺🌻🌼💐🌸🏵️"
        title={<>Fiesta Event Decor <strong>– Special Events, Beautifully Designed</strong></>}
        subtitle="Fiesta Event Decor is an owner-run business based in Cohoes, New York. From wedding stages and birthday backdrops to cradle ceremonies and baby showers, each setup is designed around the occasion and the family celebrating it."
      />
      <div className="text-center pb-6">
        <Link to="/contact" className="inline-block rounded-md bg-pink-100 px-6 py-3 font-semibold text-gray-900 hover:bg-pink-200">
          Tell us about your event
        </Link>
      </div>
      <div ref={galleryRef}>
        <GallerySection selected={selected} images={feed.items} />
        <GalleryLoadMore feed={feed} />
      </div>
      <section className="bg-pink-50 px-6 py-12 text-center">
        <div className="mx-auto max-w-3xl">
          <h2 className="elegant-font text-3xl sm:text-4xl">Meet the Owner Behind Fiesta Event Decor</h2>
          <p className="mt-4 leading-relaxed text-gray-700">
            You’ll speak directly with the person who plans your décor and sets it up. She’ll ask about your space, your colors, and any family traditions you want to include, then help you decide what will work for your event.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-5xl px-6 py-12">
        <h2 className="text-center elegant-font text-3xl sm:text-4xl">Browse Décor by Event</h2>
        <div className="mt-7 grid gap-6 sm:grid-cols-3">
          {[
            ["Weddings", "Explore floral backdrops and stage décor for your ceremony or reception.", "/weddings"],
            ["Birthday Parties", "See themed backdrops and balloon arrangements for milestone birthdays.", "/events/birthday"],
            ["Cradle Ceremonies", "Find inspiration for a family celebration that honors your traditions.", "/events/cradle"],
          ].map(([title, description, to]) => (
            <Link key={to} to={to} className="rounded-lg border border-pink-100 p-5 hover:bg-pink-50">
              <h3 className="elegant-font text-2xl">{title}</h3>
              <p className="mt-2 text-gray-700">{description}</p>
            </Link>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-6 pb-12 text-center">
        <h2 className="elegant-font text-3xl sm:text-4xl">Let’s Plan Your Décor</h2>
        <p className="mt-4 leading-relaxed text-gray-700">
          Send your date, venue or town, and the type of celebration you’re planning. If a photo in one of our galleries catches your eye, mention it in your inquiry. It gives us a starting point for discussing what will fit your space and budget.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-3">
          <Link to="/contact" className="font-semibold text-gray-900 underline underline-offset-4">Request a quote</Link>
          <Link to="/testimonials" className="font-semibold text-gray-900 underline underline-offset-4">Read client reviews</Link>
        </div>
      </section>
    </div>
  );
}
